import { getHub } from '@/backend/hubs'
import { isAllAvailable, type Eventually } from '@/data/clientUtils'
import { useHubStore } from '@/data/Hubs/useHubStore'
import { wsTopic } from '@/data/websocketTopics'
import { serializeProjectInList } from '@/modules/Projects/serializers'
import { invariant, nonNullable } from '@/shared/utils/typeAssertions'
import { useWebSocketTopic } from '@/sharedComposables/useWebSocketTopic'
import { useQuery } from '@tanstack/vue-query'
import { computed, toValue } from 'vue'

/**
 * Fetches a single hub by ID
 */
export function useHubQuery(props: { workspaceId: Eventually<string>; hubId: Eventually<string> }) {
  const hubStore = useHubStore()
  const workspaceTopic = computed(() => wsTopic.workspace(props.workspaceId))

  // Subscribe to workspace updates
  const { subscribe } = useWebSocketTopic(workspaceTopic)
  subscribe('project:updated', (data) => {
    hubStore.patch(serializeProjectInList(data))
  })

  const query = useQuery({
    queryKey: [props.workspaceId, 'hub', props.hubId],
    enabled: isAllAvailable([() => props.workspaceId, () => props.hubId]),
    staleTime: 10_000,
    queryFn: async () => {
      const workspaceId = toValue(props.workspaceId)
      const hubIdValue = nonNullable(toValue(props.hubId))
      invariant(workspaceId)
      const result = await getHub(workspaceId, hubIdValue)
      if (!result.ok) throw new Error(result.error.message)
      hubStore.upsertRaw(result.data)

      // Return the raw data
      return result.data
    },
  })

  const hub = computed(() => {
    if (!query.data.value) {
      return
    }
    return hubStore.hubs.get(query.data.value.id)
  })

  return { ...query, hub }
}
